/* All FAQ layouts share the existing phone disclosure controller. */
(() => {
  if (window.MHFaqCollapse) return;
  window.MHFaqCollapse = true;
  let sequence = 0;
  const initialized = new WeakSet();
  // Legacy form content keeps its own desktop typography.
  const rootSelector = '[data-mh-faq], [data-mh-faq-disclosure]';
  const navigation = window.performance?.getEntriesByType?.('navigation')[0];
  let initialAnchorPending = !['reload', 'back_forward'].includes(navigation?.type);
  const refresh = () => document.dispatchEvent(new CustomEvent('mh:collapse:refresh'));

  function reveal(target) {
    const group = target?.closest('[data-mh-faq-fold]') ||
      (target?.matches('[data-mh-faq-spacing]') && target.querySelector('[data-mh-faq-fold]'));
    if (!group) return false;
    group.dataset.mhCollapseOpen = 'true';
    // A question/answer anchor must reveal both levels of disclosure.
    let details = target.closest('details');
    while (details && group.contains(details)) {
      details.open = true;
      details = details.parentElement.closest('details');
    }
    refresh();
    return true;
  }

  function anchorTarget() {
    try { return document.getElementById(decodeURIComponent(location.hash.slice(1))); }
    catch { return null; }
  }

  function markSpacing(group) {
    let shell = group;
    let child = group;
    // Only a dedicated FAQ section owns the outside spacing; never shrink a
    // product, form, or article section which also contains other content.
    while (child.parentElement && !child.parentElement.matches('main, article, dialog, .shopify-section')) {
      const parent = child.parentElement;
      if (![...parent.children].every(el => el === child || el.matches('script, style, link, template'))) break;
      if (parent.matches('section.mh-sect, section.mh-faq, section.mh-space-faq, section.mh-customprojects__faq-section')) shell = parent;
      child = parent;
    }
    shell.setAttribute('data-mh-faq-spacing', '');
    if (shell !== group) group.setAttribute('data-mh-faq-inset', '');
  }

  function enhance(root) {
    if (initialized.has(root)) return false;
    const heading = root.querySelector('[data-mh-faq-heading]');
    let group = root;
    let toggle;
    let panel;

    if (!heading) {
      // Form FAQs already have a disclosure button outside the FAQ content.
      if (!root.matches('[data-mh-collapse-panel]')) return false;
      group = root.closest('[data-mh-collapse]');
      toggle = group?.querySelector('[data-mh-collapse-toggle]');
      if (!toggle || toggle.getAttribute('aria-controls') !== root.id) return false;
      panel = root;
    } else {
      toggle = heading.querySelector('[data-mh-collapse-toggle]');
      panel = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel || !root.contains(panel)) {
        // Keep the heading's header (and a dialog's close button) outside the
        // disclosure. Move existing nodes rather than replacing their HTML.
        let header = heading;
        while (header.parentElement !== root) header = header.parentElement;
        if (!header.nextElementSibling) return false;
        panel = document.createElement('div');
        panel.className = 'mh-faq-collapse__panel';
        do { panel.id = `mh-faq-collapse-${++sequence}`; } while (document.getElementById(panel.id));
        while (header.nextSibling) panel.append(header.nextSibling);
        root.append(panel);
        header.setAttribute('data-mh-faq-head', '');

        toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'mh-collapse__toggle mh-faq-collapse__toggle';
        const label = document.createElement('span');
        label.append(...heading.childNodes);
        toggle.append(label);
        heading.append(toggle);
      }
    }

    initialized.add(root);
    group.setAttribute('data-mh-faq-fold', '');
    group.setAttribute('data-mh-collapse', '');
    group.dataset.mhCollapseOpen = 'false';
    toggle.setAttribute('data-mh-collapse-toggle', '');
    toggle.setAttribute('data-mh-faq-toggle', '');
    toggle.setAttribute('aria-controls', panel.id);
    panel.setAttribute('data-mh-collapse-panel', '');
    panel.setAttribute('data-mh-faq-content', '');
    toggle.querySelectorAll('br').forEach(br => {
      br.before(document.createTextNode(' '));
      br.setAttribute('data-mh-faq-break', '');
    });
    const caret = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    caret.setAttribute('viewBox', '0 0 16 16');
    caret.setAttribute('aria-hidden', 'true');
    caret.classList.add('mh-faq-collapse__caret');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'm3 6 5 5 5-5');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    caret.append(path);
    toggle.append(caret);
    markSpacing(group);
    return true;
  }

  function run(scope = document) {
    const roots = [...scope.querySelectorAll(rootSelector)];
    if (scope.matches?.(rootSelector)) roots.unshift(scope);
    let changed = false;
    roots.forEach(root => { changed = enhance(root) || changed; });
    if (changed) refresh();
    if (initialAnchorPending) {
      const target = anchorTarget();
      if (reveal(target)) {
        initialAnchorPending = false;
        requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
      }
    }
  }

  function start() {
    run();
    const main = document.getElementById('MainContent');
    if (!main) return;
    // Help prose and package FAQs can be generated after the initial render.
    // Observe added nodes only; hidden/aria updates must not reset open state.
    new MutationObserver(records => {
      const scopes = new Set();
      records.forEach(record => {
        if (!record.addedNodes.length) return;
        const owner = record.target.closest?.(rootSelector);
        if (owner && !initialized.has(owner)) scopes.add(owner);
        record.addedNodes.forEach(node => {
          if (node.nodeType === 1 && !node.matches('svg, path')) scopes.add(node);
        });
      });
      scopes.forEach(scope => run(scope));
    }).observe(main, { childList: true, subtree: true });
  }

  document.addEventListener('click', event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname || !url.hash) return;
    try { reveal(document.getElementById(decodeURIComponent(url.hash.slice(1)))); } catch { /* Invalid fragment. */ }
  });
  window.addEventListener('hashchange', () => { initialAnchorPending = false; reveal(anchorTarget()); });
  document.addEventListener('shopify:section:load', event => run(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
